import {
  Columns3,
  LayoutDashboard,
  LogOut,
  type LucideIcon,
  Network,
  PanelLeft,
  ScrollText,
  Settings,
  ShieldCheck,
  Tag,
  UsersRound,
  Workflow,
} from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { NavLink } from 'react-router-dom';

import logoBlack from '@/assets/brand/logo-black.png';
import logoSmall from '@/assets/brand/logo-small.png';
import documentIcon from '@/assets/icons/document.svg';
// import mobileIcon from '@/assets/icons/mobile.svg'; // SDKs item hidden — see footerItems.
import whatsappIcon from '@/assets/icons/whatsapp.svg';
import { useLogout } from '@/features/auth/api/useLogout';
import { useAuthStore } from '@/features/auth/store/authStore';
import { useMyPermissions } from '@/hooks/useMyPermissions';
import { cn } from '@/lib/utils';
import { useUiStore } from '@/stores/uiStore';

import { TestModeToggle } from './TestModeToggle';

// Optional permission code required to see this item. Omit for items that
// every authenticated member can access (e.g. Dashboard).
const primaryItems: Array<{ to: string; label: string; Icon: LucideIcon; permission?: string }> = [
  { to: '/dashboard', label: 'Dashboard', Icon: LayoutDashboard },
  { to: '/workflows', label: 'Workflows', Icon: Workflow, permission: 'Workflow.View' },
  {
    to: '/questionnaires',
    label: 'Questionnaires',
    Icon: ScrollText,
    permission: 'Questionnaire.View',
  },
  { to: '/sessions', label: 'Sessions', Icon: Network, permission: 'Session.View' },
  { to: '/users', label: 'Users', Icon: UsersRound, permission: 'User.View' },
  { to: '/user-roles', label: 'User Roles', Icon: ShieldCheck, permission: 'Role.View' },
  { to: '/customization', label: 'Customization', Icon: Columns3 },
  { to: '/settings', label: 'Settings', Icon: Settings },
];

// Platform (SuperAdmin) nav. A SuperAdmin has no tenant, so the tenant-scoped
// primary items don't apply — they get their own minimal platform menu. For now
// that's just API Pricing (mirrors the Console's focused admin sidebar).
const superAdminItems: Array<{ to: string; label: string; Icon: LucideIcon; permission?: string }> =
  [{ to: '/admin', label: 'API Pricing', Icon: Tag }];

// Footer nav. Custom SVGs rather than lucide so the artwork matches the
// designer's monochrome icon set exactly. These point to EXTERNAL SignCare
// properties (docs + support portal), so they open in a new tab instead of
// routing inside the app.
const footerItems: Array<{ href: string; label: string; iconSrc: string }> = [
  { href: 'https://docs.signcare.io/', label: 'Documentation', iconSrc: documentIcon },
  // SDKs — hidden for now (SDK portal not published yet). Re-enable by
  // restoring the mobileIcon import above and this row.
  // { href: '/sdks', label: 'SDKs', iconSrc: mobileIcon },
  {
    href: 'https://support.signcare.io/portal/en/newticket',
    label: 'Support',
    iconSrc: whatsappIcon,
  },
];

// Side rail used by every authenticated screen. Three behaviors:
//   * Desktop (lg+) full: in-flow flex child, 265 px wide, sticky top-0 so
//     it stays pinned at the viewport edge as the page scrolls. Above any
//     EmailVerificationBanner, the sidebar starts below the banner because
//     AppShell renders the banner before this flex row.
//   * Desktop (lg+) collapsed: 68 px column with logo-small + icon-only
//     pill nav. Preference persists to localStorage (uiStore + persist).
//   * Mobile (<lg): off-canvas drawer (`position: fixed`) sliding in from
//     the inline-start edge. The flex parent stops reserving space.
export function Sidebar() {
  const sidebarCollapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const mobileSidebarOpen = useUiStore((s) => s.mobileSidebarOpen);
  const setMobileSidebarOpen = useUiStore((s) => s.setMobileSidebarOpen);
  const hasPermission = useMyPermissions();
  const logout = useLogout();
  const isSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin ?? false);
  // SuperAdmin sees only the platform menu (API Pricing); everyone else sees the
  // tenant menu filtered by their permissions.
  const visibleItems = isSuperAdmin
    ? superAdminItems
    : primaryItems.filter((item) => !item.permission || hasPermission(item.permission));
  // Test Mode is a tenant-admin control; SuperAdmin has no tenant, so hide it.
  const canUseTestMode = !isSuperAdmin && hasPermission('Tenant.Edit');

  return (
    <aside
      className={cn(
        'sidebar bg-white border-e border-grayEF flex flex-col z-10',
        // Desktop: in-flow flex child, sticky to viewport top so it stays
        // visible while the body scrolls. Animate the 265 ⇄ 68 px collapse.
        'lg:sticky lg:top-0 lg:self-start lg:h-dvh',
        'lg:w-[265px] shrink-0',
        'lg:transition-[width] lg:duration-300 lg:ease-out',
        sidebarCollapsed && 'lg:w-[68px]',
        // Mobile: fixed off-canvas drawer that overlays content.
        'max-lg:fixed max-lg:top-0 max-lg:h-dvh max-lg:w-[265px]',
        'max-lg:transition-[inset-inline-start] max-lg:duration-300',
        mobileSidebarOpen ? 'max-lg:start-0' : 'max-lg:-start-[265px]',
        // Group target so descendant elements can react to the collapsed
        // state via group-[.small]/sidebar utilities.
        'group/sidebar',
        sidebarCollapsed && 'small',
      )}
      aria-label="Primary navigation"
    >
      {/* Logo row + THE desktop collapse toggle — the only control that collapses or
          expands the rail, and it never leaves this row.

          It used to vanish when collapsed, handing the expand job to a ChevronLeft in the
          Topbar. So the control moved between two places AND turned into a left-chevron
          beside the page title, which reads as "go back" everywhere else in the product.

          Collapsed, the rail is 68px — about 40px after padding, which fits the brand mark
          OR the button, not both. So the header keeps the MARK and the toggle moves to the
          top of the nav list below (see "Expand rail"), where it is permanently visible.

          Two rejected alternatives, for the record:
            • fading the toggle in on hover over the mark — nothing on screen then told a
              first-time user the rail could be expanded at all;
            • stacking mark over toggle in this row — 28+6+28 plus padding is ~96px against
              the row's 70px, so the header would outgrow the Topbar it lines up with and
              leave a visible step in the shared shadow line. */}
      <div className="px-3.5 py-3 lg:py-4 shadow-elevation-1 flex items-center justify-between lg:min-h-[70px]">
        {/* Full wordmark. Hidden on a desktop-collapsed rail, where the mark replaces it;
            the mobile drawer is full width, so it shows there regardless of the desktop
            collapse state. */}
        <div
          className={cn(
            'max-h-8 lg:max-h-9.5 h-full flex items-center',
            sidebarCollapsed && 'lg:hidden',
          )}
        >
          <img src={logoBlack} alt="SignCare" className="object-contain h-full" />
        </div>

        {/* The mark — desktop-collapsed rail only, centred in the row on its own. */}
        {sidebarCollapsed && (
          <img
            src={logoSmall}
            alt="SignCare"
            className="hidden lg:block mx-auto max-h-7 shrink-0 object-contain"
          />
        )}

        {/* Collapse. Expanded rail only: collapsed, the nav-area button below expands. */}
        <button
          type="button"
          onClick={toggleSidebar}
          aria-label="Collapse sidebar"
          title="Collapse sidebar"
          className={cn(
            'w-7.5 h-7.5 rounded-full hover:bg-placeholder/15 flex items-center justify-center transition-all duration-300 shrink-0',
            // Hidden on mobile (the drawer has the Topbar's own toggle).
            'max-lg:hidden',
            sidebarCollapsed && 'lg:hidden',
          )}
        >
          <PanelLeft className="w-4.5 h-4.5 text-icon-muted" />
        </button>
      </div>

      {/* Scrollable nav + footer */}
      <div
        className={cn(
          'p-5 lg:pt-6 lg:pb-7.5 flex-1 min-h-0 flex flex-col',
          sidebarCollapsed && 'lg:p-4',
        )}
      >
        {/* py-px: `overflow-y: auto` implicitly clips on the X axis too
            (CSS spec — `auto` + `visible` resolves to auto both ways), so
            the active pill's `border-primary` top edge would sit exactly
            on the clip boundary and lose 1px to sub-pixel rounding on
            page scroll. A 1 px vertical padding pushes the content off
            that boundary without changing the visible layout. */}
        {/* Expand rail — collapsed desktop only, and permanently visible.
            Sits at the top of the nav list, styled as a rail item and separated by a rule
            so it reads as an action on the rail rather than a destination in it. This is
            where the expand control lives when collapsed, because the header row is only
            wide enough for the brand mark. */}
        {sidebarCollapsed && (
          <div className="hidden lg:block border-b border-grayEF pb-2 mb-2">
            <button
              type="button"
              onClick={toggleSidebar}
              aria-label="Expand sidebar"
              title="Expand sidebar"
              className="flex h-[35px] w-[35px] items-center justify-center rounded-full border border-transparent text-black-logo transition-all duration-300 hover:border-placeholder"
            >
              <PanelLeft className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto hide-scrollbar py-px">
          <ul className="flex flex-col gap-2">
            {visibleItems.map((item) => (
              <li key={item.to}>
                <SidebarLink
                  to={item.to}
                  label={item.label}
                  icon={<item.Icon className="w-4 h-4" />}
                  collapsed={sidebarCollapsed}
                  onNavigate={() => setMobileSidebarOpen(false)}
                />
              </li>
            ))}
          </ul>
        </div>

        {canUseTestMode && (
          <div className="border-t border-grayEF pt-3 mt-2">
            <TestModeToggle collapsed={sidebarCollapsed} />
          </div>
        )}

        <div className="border-t border-grayEF pt-8 bg-white">
          <ul className="flex flex-col gap-0.5 py-[9px]">
            {footerItems.map((item) => (
              <li key={item.href}>
                <SidebarExternalLink
                  href={item.href}
                  label={item.label}
                  icon={<img src={item.iconSrc} alt="" className="w-4 h-4" aria-hidden />}
                  collapsed={sidebarCollapsed}
                  onNavigate={() => setMobileSidebarOpen(false)}
                />
              </li>
            ))}
          </ul>

          {/* Log Out — the account action, at the very bottom of the rail.
              It used to be the only working item in the Topbar's avatar menu, which put a
              destructive action one click away in every page header. Down here it is still always
              reachable but is not competing with the page's own controls. */}
          <div className="border-t border-grayEF pt-2 mt-1">
            <button
              type="button"
              onClick={() => {
                setMobileSidebarOpen(false);
                logout();
              }}
              title="Log Out"
              className={cn(
                'flex w-full items-center gap-2 rounded-lg border border-transparent px-2 py-[5px] text-sm leading-[150%] text-destructive transition-all duration-300 hover:border-destructive/40 hover:bg-destructive/5',
                sidebarCollapsed &&
                  'lg:h-[35px] lg:w-[35px] lg:justify-center lg:rounded-full lg:px-0',
              )}
            >
              <LogOut className="h-4 w-4 shrink-0" />
              <span className={cn(sidebarCollapsed && 'lg:hidden')}>Log Out</span>
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
}

interface SidebarLinkProps {
  to: ComponentProps<typeof NavLink>['to'];
  label: string;
  icon: ReactNode;
  collapsed: boolean;
  onNavigate: () => void;
}

function SidebarLink({ to, label, icon, collapsed, onNavigate }: SidebarLinkProps) {
  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2 px-2 py-[5px] rounded-lg text-sm leading-[150%] border border-transparent text-black-logo hover:border-placeholder transition-all duration-300',
          // Collapsed: square pill, icon-only.
          collapsed && 'lg:h-[35px] lg:w-[35px] lg:justify-center lg:rounded-full lg:px-0',
          isActive && 'text-primary border-primary font-semibold',
        )
      }
      end
    >
      {icon}
      <span className={cn(collapsed && 'lg:hidden')}>{label}</span>
    </NavLink>
  );
}

interface SidebarExternalLinkProps {
  href: string;
  label: string;
  icon: ReactNode;
  collapsed: boolean;
  onNavigate: () => void;
}

// Footer link to an external SignCare property (docs / support). Same styling
// as SidebarLink but renders a plain anchor that opens in a new tab — there is
// no in-app route and thus no active state.
function SidebarExternalLink({
  href,
  label,
  icon,
  collapsed,
  onNavigate,
}: SidebarExternalLinkProps) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={onNavigate}
      className={cn(
        'flex items-center gap-2 px-2 py-[5px] rounded-lg text-sm leading-[150%] border border-transparent text-black-logo hover:border-placeholder transition-all duration-300',
        collapsed && 'lg:h-[35px] lg:w-[35px] lg:justify-center lg:rounded-full lg:px-0',
      )}
    >
      {icon}
      <span className={cn(collapsed && 'lg:hidden')}>{label}</span>
    </a>
  );
}
